import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { IconBack, IconClose, InformationalScreen, LoadingModal, Stack, } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ErrorCode } from 'Constants';
import { useApiError } from 'Hooks';
import { Button, Footer, MainContainer, OptInOut, RichText } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const mapStateToProps = ({ env: { osType, osVersion, app, appVersion }, auth, nav: { query }, }) => {
    return {
        osType,
        osVersion,
        app,
        appVersion,
        auth,
        isAccountProfile: !!query.accountProfile,
    };
};
const connector = connect(mapStateToProps);
const LibreAssistConsent = ({ osType, osVersion, app, appVersion, auth, isAccountProfile, }) => {
    const [loading, setLoading] = useState(false);
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const handleConsentWrapper = useCallback(async (action) => {
        return new Promise((resolve, reject) => {
            handleConsent(auth.account_id, action, osType, osVersion, app, appVersion, 'assist')
                .then((resp) => {
                const updatedAuthSession = {
                    ...auth,
                    include: {
                        patient: {
                            ...auth.include.patient,
                            consents: { ...auth.include.patient.consents, assist: { ...resp } },
                        },
                    },
                };
                notifyApp('adc-webview:consents', {
                    id: 'assist',
                    action,
                    location: isAccountProfile ? 'profile' : 'tutorial',
                });
                store.dispatch({ type: AUTH_SESSION, authSession: updatedAuthSession });
                resolve(updatedAuthSession);
            })
                .catch((error) => {
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
                reject(error);
            });
        });
    }, [
        app,
        appVersion,
        auth,
        isAccountProfile,
        osType,
        osVersion,
        showApiErrorModal,
        showNetworkErrorModal,
    ]);
    const handleSubmitAction = useCallback(async (action) => {
        try {
            setLoading(true);
            await handleConsentWrapper(action);
            if (isAccountProfile) {
                // Ends the loading state if the user is on the account profile page
                setLoading(false);
                return;
            }
            // This timeout is added to give the app time to redirect the user back.
            setTimeout(() => {
                setLoading(false);
            }, APP_RETURN_TIMEOUT);
        }
        catch {
            setLoading(false);
        }
    }, [handleConsentWrapper, isAccountProfile]);
    const onDeclineButtonClick = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('Consents.modals.declineAssistConsentDialog.body'),
                secondaryAction: {
                    label: i18n.t('Global.microcopy.common.goBack'),
                    onPress: () => {
                        store.dispatch({
                            type: CLOSE_MODAL,
                        });
                    },
                },
                primaryAction: {
                    label: i18n.t('Global.microcopy.common.decline'),
                    onPress: () => {
                        handleSubmitAction('decline');
                        store.dispatch({
                            type: CLOSE_MODAL,
                        });
                    },
                    destructive: true,
                },
                buttonLayout: 'column',
                testID: 'LibreAssist',
            },
        });
    }, [handleSubmitAction]);
    return (React.createElement(MainContainer, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(InformationalScreen, { topNavigationProps: {
                animatedTitle: true,
                title: i18n.t('Consents.content.assistDataUse.primaryText'),
                buttonStart: {
                    element: isAccountProfile ? (React.createElement(IconBack, { testID: "LibreAssist.close-Pressable" })) : (React.createElement(IconClose, { testID: "LibreAssist.close-Pressable" })),
                    label: i18n.t('LibreAssist.close'),
                    onPress: () => mediator.publish('router:back'),
                },
            }, testID: "LibreAssist" },
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(RichText, { testID: "Consents.content.assistDataUse.bodyText", text: i18n.t('Consents.content.assistDataUse.bodyText') })),
                React.createElement(InformationalScreen.Footer, null, isAccountProfile ? (React.createElement(OptInOut, { optionStrings: {
                        titleTag: 'Global.microcopy.common.status',
                        positiveTag: 'Global.microcopy.common.accepted',
                        negativeTag: 'Global.microcopy.common.declined',
                        positiveOption: 'Global.microcopy.common.accept',
                        negativeOption: 'Global.microcopy.common.decline',
                    }, initialState: !!auth.include.patient?.consents?.assist?.accepted ? 'accept' : 'decline', handleConsent: handleConsentWrapper, onDecline: onDeclineButtonClick })) : (React.createElement(Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4", marginBottom: "$4" },
                        React.createElement(Button, { onPress: () => handleSubmitAction('accept'), testID: "LibreAssist.accept", label: i18n.t('Global.microcopy.common.accept'), "aria-label": i18n.t('Global.microcopy.common.accept') }),
                        React.createElement(Button, { variant: "secondary", onPress: onDeclineButtonClick, label: i18n.t('Global.microcopy.common.decline'), "aria-label": i18n.t('Global.microcopy.common.decline'), testID: "LibreAssist.decline" })))))))));
};
export default connector(LibreAssistConsent);
